/* ---------------------------------------------------------------------------
   Playback: the original, then everybody's dub.

   Video is never muxed or re-encoded. A dub is one <video> playing muted while
   the performer's recorded sub-chunks are scheduled against the video timeline
   through Web Audio. Chunks are timestamps, not files.

   Drift is the enemy: the element clock and the AudioContext clock run
   independently, so we anchor them on the first presented frame and re-anchor
   whenever they disagree or the user seeks.
   --------------------------------------------------------------------------- */

import type { SubChunk } from "../../convex/lib/game";
import { decodeAudio, getAudioContext, isAudioSupported, resumeIfSuspended } from "./audio";
import {
  FRAME_EPSILON,
  MediaPlaybackError,
  playUntil,
  seekTo,
  type PlayUntilReason,
} from "./videoTiming";

/** Re-exported so the playback screen has one import site for the iOS unlock. */
export { primeVideoForPlayback, MediaPlaybackError } from "./videoTiming";

export interface DubProgress {
  /** Absolute position in the source clip. */
  currentTime: number;
  /** Seconds into the dubbed segment. */
  elapsed: number;
  /** Length of the dubbed segment. */
  duration: number;
  progress: number;
  /** Sub-chunk currently on screen, or -1 outside the segment. */
  subChunkIndex: number;
  /** Signed audio-vs-picture drift in seconds, after correction. */
  driftSec: number;
}

export interface PlaybackResult {
  completed: boolean;
  reason: "ended" | "aborted";
}

export interface DubPlaybackOptions {
  /** Abort mid-dub (round advanced, player left). */
  signal?: AbortSignal;
  onProgress?: (progress: DubProgress) => void;
  /** Output gain for the dub, 0..1. Default 1. */
  gain?: number;
  /** Re-anchor once picture and audio drift past this. Default 0.12s. */
  resyncToleranceSec?: number;
}

export interface OriginalPlaybackOptions {
  /** Abort playback — this is what the skip vote pulls. */
  signal?: AbortSignal;
  onProgress?: (progress: DubProgress) => void;
  /** Element volume, 0..1. Default 1. */
  volume?: number;
}

export interface OriginalSegment {
  start: number;
  end: number;
}

const DEFAULT_RESYNC_TOLERANCE = 0.12;

interface ScheduledTrack {
  buffer: AudioBuffer;
  /** Absolute video time this buffer should begin at. */
  startsAt: number;
}

function toResult(reason: PlayUntilReason): PlaybackResult {
  return reason === "aborted"
    ? { completed: false, reason: "aborted" }
    : { completed: true, reason: "ended" };
}

function subChunkAt(subChunks: SubChunk[], time: number): number {
  for (const chunk of subChunks) {
    if (time >= chunk.start - FRAME_EPSILON && time < chunk.end) return chunk.index;
  }
  return -1;
}

/**
 * Play a dub: video muted, recorded audio scheduled against the video clock.
 *
 * `audio` is either one buffer per sub-chunk (in order) or a single buffer
 * covering the whole segment. Anything the recorder produced can be turned into
 * buffers with `loadDubAudio`.
 *
 * Call this from a user gesture the first time in a session — the shared
 * AudioContext must already be unlocked (see `unlockAudio`).
 */
export async function playDub(
  videoEl: HTMLVideoElement,
  audio: AudioBuffer | AudioBuffer[],
  subChunks: SubChunk[],
  options: DubPlaybackOptions = {},
): Promise<PlaybackResult> {
  if (subChunks.length === 0) {
    throw new Error("playDub needs at least one sub-chunk to know where the segment is.");
  }

  const {
    signal,
    onProgress,
    gain = 1,
    resyncToleranceSec = DEFAULT_RESYNC_TOLERANCE,
  } = options;

  const buffers = Array.isArray(audio) ? audio : [audio];
  const segmentStart = subChunks[0].start;
  const segmentEnd = subChunks[subChunks.length - 1].end;
  const duration = segmentEnd - segmentStart;

  const tracks: ScheduledTrack[] =
    buffers.length === 1
      ? [{ buffer: buffers[0], startsAt: segmentStart }]
      : buffers
          .slice(0, subChunks.length)
          .map((buffer, i) => ({ buffer, startsAt: subChunks[i].start }));

  videoEl.playsInline = true;
  videoEl.setAttribute("playsinline", "");
  videoEl.muted = true;
  videoEl.volume = 0;

  let ctx: AudioContext | null = null;
  let output: GainNode | null = null;
  if (isAudioSupported() && tracks.length > 0) {
    // Build the context first, THEN test it. Testing the state before it exists
    // reports "uninitialized" and would reject a desktop player who reached
    // playback without ever recording — there a fresh context starts running on
    // its own. On iOS it starts suspended, which is the case we do want to catch.
    ctx = getAudioContext();
    if (ctx.state !== "running") await resumeIfSuspended();

    // Still not running: the session was never unlocked on a real gesture, or
    // iOS interrupted it. Playing on regardless would show the picture with no
    // dub over it and report success — a silent failure the player reads as
    // "this round is broken". Fail loudly so the UI can re-prompt with a tap.
    if (ctx.state !== "running") {
      throw new MediaPlaybackError(
        "Sound is locked on this device. Tap to enable audio, then play again.",
        "blocked",
      );
    }
    output = ctx.createGain();
    output.gain.value = gain;
    output.connect(ctx.destination);
  }

  let sources: AudioBufferSourceNode[] = [];
  let anchorCtx = 0;
  let anchorVideo = 0;
  let anchored = false;

  const stopAll = () => {
    for (const source of sources) {
      try {
        source.onended = null;
        source.stop();
      } catch {
        /* Never started, or already finished. */
      }
      source.disconnect();
    }
    sources = [];
  };

  /** (Re-)schedule every track relative to `videoTime`, which is now. */
  const schedule = (videoTime: number) => {
    if (!ctx || !output) return;
    stopAll();
    anchorCtx = ctx.currentTime;
    anchorVideo = videoTime;
    anchored = true;

    for (const track of tracks) {
      const offset = videoTime - track.startsAt;
      if (offset >= track.buffer.duration - FRAME_EPSILON) continue; // Already played out.
      const source = ctx.createBufferSource();
      source.buffer = track.buffer;
      source.connect(output);
      if (offset <= 0) {
        source.start(anchorCtx - offset); // Still in the future.
      } else {
        source.start(anchorCtx, offset); // Mid-chunk: skip into the buffer.
      }
      sources.push(source);
    }
  };

  const onFrame = (mediaTime: number) => {
    let drift = 0;
    if (ctx) {
      if (!anchored) {
        schedule(mediaTime);
      } else {
        const expected = anchorVideo + (ctx.currentTime - anchorCtx);
        drift = mediaTime - expected;
        if (Math.abs(drift) > resyncToleranceSec) schedule(mediaTime);
      }
    }
    onProgress?.({
      currentTime: mediaTime,
      elapsed: Math.max(0, mediaTime - segmentStart),
      duration,
      progress: duration > 0 ? Math.min(1, Math.max(0, (mediaTime - segmentStart) / duration)) : 0,
      subChunkIndex: subChunkAt(subChunks, mediaTime),
      driftSec: drift,
    });
  };

  const onSeeked = () => {
    if (anchored && !videoEl.paused) schedule(videoEl.currentTime);
  };
  const onPause = () => stopAll();

  await seekTo(videoEl, segmentStart, { signal });
  videoEl.addEventListener("seeked", onSeeked);
  videoEl.addEventListener("pause", onPause);

  try {
    const reason = await playUntil(videoEl, segmentEnd, { signal, onFrame });
    return toResult(reason);
  } finally {
    videoEl.removeEventListener("seeked", onSeeked);
    videoEl.removeEventListener("pause", onPause);
    stopAll();
    output?.disconnect();
  }
}

/**
 * Play the original segment with its own audio on.
 *
 * `options.signal` is the skip vote: abort it and playback stops immediately,
 * resolving with `{ completed: false, reason: "aborted" }`.
 *
 * iOS: this plays UNMUTED after an awaited seek, which is off the gesture task,
 * so the element must already be primed. Call `primeVideoForPlayback(videoEl)`
 * synchronously in the tap handler that starts the playback phase — it is
 * re-exported from this module for exactly that. An unprimed element rejects
 * with `MediaPlaybackError` code `"blocked"`; catch it and show a tap-to-play
 * affordance rather than treating it as a broken round.
 */
export async function playOriginal(
  videoEl: HTMLVideoElement,
  segment: OriginalSegment,
  options: OriginalPlaybackOptions = {},
): Promise<PlaybackResult> {
  const { signal, onProgress, volume = 1 } = options;
  const duration = segment.end - segment.start;

  videoEl.playsInline = true;
  videoEl.setAttribute("playsinline", "");
  await resumeIfSuspended();

  await seekTo(videoEl, segment.start, { signal });
  videoEl.muted = false;
  videoEl.volume = volume;

  try {
    const reason = await playUntil(videoEl, segment.end, {
      signal,
      onFrame: onProgress
        ? (mediaTime) =>
            onProgress({
              currentTime: mediaTime,
              elapsed: Math.max(0, mediaTime - segment.start),
              duration,
              progress:
                duration > 0
                  ? Math.min(1, Math.max(0, (mediaTime - segment.start) / duration))
                  : 0,
              subChunkIndex: -1,
              driftSec: 0,
            })
        : undefined,
    });
    return toResult(reason);
  } finally {
    videoEl.pause();
    videoEl.muted = true;
  }
}

/**
 * Turn recorded sub-chunks into AudioBuffers ready for `playDub`.
 * Accepts blobs straight off the recorder, raw bytes, or R2 URLs.
 */
export async function loadDubAudio(
  sources: Array<Blob | ArrayBuffer | string>,
  options: { signal?: AbortSignal } = {},
): Promise<AudioBuffer[]> {
  return Promise.all(
    sources.map(async (source) => {
      if (typeof source === "string") {
        const init: RequestInit = options.signal ? { signal: options.signal } : {};
        const response = await fetch(source, init);
        if (!response.ok) {
          throw new Error(`Couldn't fetch a recorded chunk (HTTP ${response.status}).`);
        }
        return decodeAudio(await response.arrayBuffer());
      }
      if (source instanceof Blob) return decodeAudio(await source.arrayBuffer());
      return decodeAudio(source);
    }),
  );
}
